import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FilePlus, FolderOpen, HelpCircle } from "lucide-react";
import {
  PreviewSettingsProvider,
  type PreviewMode,
} from "@tgpe/editor-core";

import { ThemeProvider } from "./components/ThemeProvider.js";
import { Toaster } from "./components/Toaster.js";
import { PromoButton } from "./features/cross-promo/PromoButton.js";
import { PromoFooter } from "./features/cross-promo/PromoFooter.js";
import { DraftsScreen } from "./features/drafts/DraftsScreen.js";
import { useNewDraft } from "./features/drafts/useNewDraft.js";
import { EditorScreen } from "./features/editor/EditorScreen.js";
import { HelpScreen } from "./features/help/HelpScreen.js";
import { ShareController } from "./features/share/ShareController.js";
import { useTelegramWebApp } from "./hooks/useTelegramWebApp.js";
import { APP_VERSION } from "./lib/env.js";
import { hereticAppUrl } from "./lib/heretic.js";
import { logger } from "./lib/logger.js";
import { activeDraftIdStorage } from "./lib/storage/index.js";
import {
  StorageProvider,
  useStorage,
} from "./lib/storage/storage-context.js";
import { isInsideTelegram } from "./lib/telegram.js";
import { useDraftsStore } from "./stores/drafts-store.js";


export function App() {
  // The Mini App has no full settings UI yet, so previewMode is local
  // state with a sensible default. videoAutoplay stays off to respect
  // mobile data — same default the desktop client ships with for new
  // installs.
  const [previewMode, setPreviewMode] = useState<PreviewMode>("visual");

  return (
    <ThemeProvider>
      <PreviewSettingsProvider
        value={{ previewMode, setPreviewMode, videoAutoplay: false }}
      >
        <StorageProvider>
          <AppInner />
        </StorageProvider>
      </PreviewSettingsProvider>
    </ThemeProvider>
  );
}

function AppInner() {
  const { t } = useTranslation();
  const wa = useTelegramWebApp();
  const insideTelegram = isInsideTelegram();
  const screen = useDraftsStore((s) => s.screen);
  const setScreen = useDraftsStore((s) => s.setScreen);
  const newDraft = useNewDraft();

  useReloadRecovery();

  function openDesktopReleases() {
    const url = hereticAppUrl();
    if (wa?.openLink) wa.openLink(url);
    else window.open(url, "_blank", "noopener,noreferrer");
  }

  return (
    <div
      className="flex h-[100dvh] flex-col bg-[var(--tg-bg)] text-[var(--tg-text)]"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      <header className="flex items-center justify-between border-b border-[var(--tg-secondary-bg)] px-3 py-2">
        <div className="flex items-center gap-2">
          {screen === "editor" && (
            <>
              <button
                type="button"
                aria-label="Open drafts"
                title="Open drafts"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-[var(--tg-secondary-bg)]"
                onClick={() => {
                  setScreen("drafts");
                }}
              >
                <FolderOpen size={18} />
              </button>
              <button
                type="button"
                aria-label="New draft"
                title="New draft"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-[var(--tg-secondary-bg)]"
                onClick={() => {
                  void newDraft();
                }}
              >
                <FilePlus size={18} />
              </button>
            </>
          )}
          <h1 className="text-sm font-semibold tracking-tight">{t("app.name")}</h1>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={t("help.open")}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[var(--tg-hint)] hover:bg-[var(--tg-secondary-bg)]"
            onClick={() => {
              setScreen("help");
            }}
          >
            <HelpCircle size={18} />
          </button>
          <PromoButton />
          <button
            type="button"
            onClick={openDesktopReleases}
            className="text-xs text-[var(--tg-link)] underline-offset-2 hover:underline"
          >
            {t("footer.get_desktop")}
          </button>
        </div>
      </header>

      {!insideTelegram && (
        <div
          role="alert"
          className="border-b border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
        >
          {t("error.not_in_telegram")}
        </div>
      )}

      <main className="flex-1 overflow-hidden">
        {/* Editor and drafts stay mounted to preserve editor state. */}
        <div className={screen === "editor" ? "h-full" : "hidden"}>
          <EditorScreen />
        </div>
        <div className={screen === "drafts" ? "h-full" : "hidden"}>
          <DraftsScreen />
        </div>
        {screen === "help" && <HelpScreen />}
      </main>

      {screen === "editor" && <PromoFooter />}

      {/* MainButton is global on the WebApp; mount only on editor screen. */}
      {screen === "editor" && <ShareController />}
      <Toaster />

      <footer
        className="border-t border-[var(--tg-secondary-bg)] px-3 py-1 text-center text-[10px] text-[var(--tg-hint)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {t("footer.version", { version: APP_VERSION })}
      </footer>
    </div>
  );
}

/**
 * On Mini App load, decide what state to hydrate the editor with:
 *
 * 1. If we have an `activeDraftId` in localStorage AND CloudStorage is
 *    available → load that draft.
 * 2. Else if we have a `tgpe:current` localStorage backup → restore it
 *    silently (Epic 05 keeps the recovery prompt minimal; "save as new"
 *    UX lands in Epic 06+).
 * 3. Else → start with an empty editor.
 */
function useReloadRecovery(): void {
  const { drafts, backup, cloudAvailable } = useStorage();
  const openDraft = useDraftsStore((s) => s.openDraft);

  useEffect(() => {
    void (async () => {
      const activeId = activeDraftIdStorage.get();
      if (activeId !== null && cloudAvailable) {
        try {
          const rec = await drafts.load(activeId);
          if (rec) {
            openDraft(activeId, rec.state);
            return;
          }
        } catch (err) {
          logger.error("recovery: drafts.load failed", err);
        }
      }
      const local = backup.loadCurrent();
      if (local) {
        openDraft(null, local.state);
      }
    })();
    // Run exactly once on mount; deps are stable refs from a Zustand store
    // and the storage context.
  }, [drafts, backup, cloudAvailable, openDraft]);
}
